import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { GBP, statusColor, pretty } from "@/lib/helpers";
import { StatCard, PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ShoppingBag, CalendarClock, PhoneCall, AlertTriangle, Users, Grid3X3, PoundSterling,
} from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { rid } = useParams();
  const [d, setD] = useState(null);

  const load = useCallback(() => {
    api.get(`/restaurants/${rid}/dashboard`).then((r) => setD(r.data)).catch(() => {});
  }, [rid]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const completeHandover = async (id) => {
    await api.patch(`/call-logs/${id}/handover-complete`);
    toast.success("Handover marked complete");
    load();
  };

  if (!d) return <div className="text-muted-foreground">Loading dashboard…</div>;

  return (
    <div>
      <PageHeader title="Today at a glance" subtitle="Live view of service — orders, bookings, calls and tables." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-orders-today" label="Orders today" value={d.orders_today_count} icon={ShoppingBag} />
        <StatCard testid="stat-bookings-today" label="Bookings today" value={d.bookings_today_count} icon={CalendarClock} />
        <StatCard testid="stat-active-calls" label="Active calls" value={d.active_calls.length} icon={PhoneCall}
          accent="bg-accent/15 text-accent" />
        <StatCard testid="stat-revenue" label="Revenue (paid)" value={GBP(d.revenue_paid_today)} icon={PoundSterling}
          accent="bg-emerald-100 text-emerald-700" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testid="stat-handover" label="Handover needed" value={d.handover_calls.length} icon={AlertTriangle}
          accent="bg-red-100 text-red-700" />
        <StatCard testid="stat-review-orders" label="Allergy reviews" value={d.review_orders.length} icon={AlertTriangle}
          accent="bg-orange-100 text-orange-700" />
        <StatCard testid="stat-walkins" label="Walk-ins today" value={d.walk_ins.length} icon={Users} />
        <StatCard testid="stat-tables" label="Tables occupied" value={`${d.tables_occupied}/${d.tables.length}`} icon={Grid3X3} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Handover calls needing staff action */}
        <Card className="p-6 rounded-2xl" data-testid="handover-panel">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h3 className="font-display font-bold text-lg">AI handover — needs staff action</h3>
          </div>
          {d.handover_calls.length === 0 ? (
            <p className="text-sm text-muted-foreground">No calls awaiting handover. Nice and calm.</p>
          ) : (
            <div className="space-y-3">
              {d.handover_calls.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50/50 p-3">
                  <div>
                    <p className="font-medium">{c.caller_number}</p>
                    <p className="text-sm text-muted-foreground">{c.handover_reason || c.summary || "Handover requested"}</p>
                  </div>
                  <Button size="sm" data-testid={`handover-complete-${c.id}`} onClick={() => completeHandover(c.id)}>
                    Mark handled
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Allergy review orders */}
        <Card className="p-6 rounded-2xl" data-testid="review-panel">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <h3 className="font-display font-bold text-lg">Orders needing allergy review</h3>
          </div>
          {d.review_orders.length === 0 ? (
            <p className="text-sm text-muted-foreground">No orders flagged for review.</p>
          ) : (
            <div className="space-y-3">
              {d.review_orders.map((o) => (
                <div key={o.id} className="rounded-xl border border-orange-200 bg-orange-50/50 p-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{o.order_number} · {o.customer_name}</p>
                    <Badge className={statusColor(o.status)} variant="outline">{pretty(o.status)}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{o.allergen_disclosure_text}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Today's orders */}
        <Card className="p-6 rounded-2xl">
          <h3 className="font-display font-bold text-lg mb-4">Today's orders</h3>
          <div className="space-y-2">
            {d.orders_today.slice(0, 6).map((o) => (
              <div key={o.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div>
                  <p className="font-medium">{o.order_number} · {pretty(o.order_type)}</p>
                  <p className="text-sm text-muted-foreground">{o.customer_name} · {pretty(o.source)}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{GBP(o.total)}</p>
                  <Badge className={statusColor(o.status)} variant="outline">{pretty(o.status)}</Badge>
                </div>
              </div>
            ))}
            {d.orders_today.length === 0 && <p className="text-sm text-muted-foreground">No orders yet today.</p>}
          </div>
        </Card>

        {/* Today's bookings */}
        <Card className="p-6 rounded-2xl">
          <h3 className="font-display font-bold text-lg mb-4">Today's bookings</h3>
          <div className="space-y-2">
            {d.bookings_today.map((b) => (
              <div key={b.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div>
                  <p className="font-medium">{b.booking_time} · {b.customer_name}</p>
                  <p className="text-sm text-muted-foreground">{b.guest_count} guests · {pretty(b.source)}</p>
                </div>
                <Badge className={statusColor(b.status)} variant="outline">{pretty(b.status)}</Badge>
              </div>
            ))}
            {d.bookings_today.length === 0 && <p className="text-sm text-muted-foreground">No bookings yet today.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
